import { useEffect, useState } from 'react';
import { v } from '../constants';
import { useAuth } from '../auth/AuthContext';
import {
  cancelSubscription,
  fetchInvoices,
  fetchSubscription,
  resumeSubscription,
  type InvoiceRow,
  type SubscriptionState,
} from '../lib/billing';
import { CheckoutModal, type CheckoutKind } from './CheckoutModal';
import { getHapticsEnabled, setHapticsEnabled, Haptics } from '../lib/haptics';
import { apiFetch } from '../lib/api';

// In-app account / billing surface. White-label — change card + cancel
// happen inside the modal, no redirect to checkout.stripe.com or the
// Stripe Customer Portal.
//
// If the user has no subscription (free tier), this shows the hint
// balance + the upgrade CTA only. Once they're paying, the full
// management surface appears: status / next renewal / change card /
// cancel.

interface Props {
  onClose: () => void;
  onUpgradeRequested: () => void;
  onBuyHints: () => void;
}

export function AccountModal({ onClose, onUpgradeRequested, onBuyHints }: Props) {
  const auth = useAuth();
  const [sub, setSub] = useState<SubscriptionState | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  // Local mirror of the haptics preference. Source of truth lives in
  // localStorage; the toggle re-renders only this modal so we don't
  // need to thread it through context.
  const [hapticsOn, setHapticsOnLocal] = useState<boolean>(() => getHapticsEnabled());
  const [loading, setLoading] = useState(true);
  const [embeddedCheckout, setEmbeddedCheckout] = useState<CheckoutKind | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const reload = async () => {
    setLoading(true);
    const [s, inv] = await Promise.all([
      fetchSubscription(),
      // Fetch invoices in parallel — graceful degradation: if Stripe
      // 501s (not configured) or the user has no customer, we just
      // get an empty list. UI hides the section in that case.
      fetchInvoices().catch(() => [] as InvoiceRow[]),
    ]);
    setSub(s);
    setInvoices(inv);
    setLoading(false);
  };
  useEffect(() => { void reload(); }, []);

  // Dismiss handler — close the embedded sub-modal first, then if
  // already at top level, close the AccountModal itself.
  const handleClose = () => {
    if (embeddedCheckout) { setEmbeddedCheckout(null); return; }
    onClose();
  };

  // Ran after the change-card flow finishes; we just re-pull the
  // subscription so the cardBrand / cardLast4 surface updates.
  const onChangeCardSuccess = async () => {
    setEmbeddedCheckout(null);
    await reload();
  };

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1050, padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: v('paper'),
            color: v('ink'),
            border: `1px solid ${v('rule')}`,
            borderRadius: 18,
            padding: 24,
            maxWidth: 460, width: '100%',
            maxHeight: '90vh', overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Account</div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'transparent', border: 'none', color: v('inkMute'),
                cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 4,
              }}
            >×</button>
          </div>

          {auth.user && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{auth.user.displayName}</div>
                <div style={{ fontSize: 12, color: v('inkMute'), marginTop: 2 }}>{auth.user.email}</div>
              </div>
              <a
                href={`/u/${encodeURIComponent(auth.user.displayName)}`}
                className="hm-action"
                style={{ textDecoration: 'none', fontSize: 12, padding: '6px 10px' }}
                title="View your public profile"
              >View profile</a>
            </div>
          )}

          <Section label="Hints" right={`${auth.user?.hintsBalance ?? 0} available`}>
            <div style={{ fontSize: 12, color: v('inkSoft'), marginBottom: 10 }}>
              3 free on signup, +5 every month. Buy 25 more for $0.99 anytime.
            </div>
            <button
              className="hm-action"
              onClick={onBuyHints}
              style={primaryButtonStyle}
            >Buy 25 hints — $0.99</button>
          </Section>

          <Section label="Subscription">
            {loading ? (
              <div style={{ fontSize: 13, color: v('inkSoft') }}>Loading…</div>
            ) : auth.user?.isPremium && !sub ? (
              // Premium WITHOUT a Stripe sub — Apple-IAP subscribers,
              // hand-flipped comps, and any future non-Stripe path.
              // Show the active status but no Upgrade button (paying
              // again would be wrong) and no Stripe management UI
              // (there's nothing to manage on this side).
              <>
                <KV label="Plan" value="Premium" />
                <KV label="Status" value="Active" />
                <div style={{ fontSize: 12, color: v('inkMute'), marginTop: 10, lineHeight: 1.45 }}>
                  Premium is active on this account. If you subscribed
                  on iOS, manage your subscription in Settings → Apple ID
                  → Subscriptions.
                </div>
              </>
            ) : !sub ? (
              <>
                <div style={{ fontSize: 13, color: v('inkSoft'), marginBottom: 10 }}>
                  Free plan. Upgrade to unlock the past-day archive and Expert difficulty.
                </div>
                <button
                  className="hm-action"
                  onClick={onUpgradeRequested}
                  style={primaryButtonStyle}
                >Upgrade to Premium</button>
              </>
            ) : (
              <>
                <KV label="Plan" value={prettyPlan(sub.plan)} />
                <KV label="Status" value={prettyStatus(sub.status, sub.cancelAtPeriodEnd)} />
                {sub.currentPeriodEnd && (
                  <KV
                    label={sub.cancelAtPeriodEnd ? 'Premium ends' : 'Next renewal'}
                    value={formatDate(sub.currentPeriodEnd)}
                  />
                )}
                {sub.cardBrand && sub.cardLast4 && (
                  <KV label="Card on file" value={`${sub.cardBrand.toUpperCase()} ···· ${sub.cardLast4}`} />
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button
                    className="hm-action"
                    onClick={() => setEmbeddedCheckout({ kind: 'change_card' })}
                    style={secondaryButtonStyle}
                  >Update payment method</button>
                  {sub.cancelAtPeriodEnd ? (
                    <button
                      className="hm-action"
                      onClick={async () => {
                        setActionInFlight(true);
                        const ok = await resumeSubscription();
                        setActionInFlight(false);
                        if (ok) await reload();
                      }}
                      disabled={actionInFlight}
                      style={primaryButtonStyle}
                    >Resume subscription</button>
                  ) : (
                    <button
                      className="hm-action"
                      onClick={() => setConfirmingCancel(true)}
                      style={dangerButtonStyle}
                    >Cancel subscription</button>
                  )}
                </div>
              </>
            )}
          </Section>

          {/* Invoices — Stripe-billed receipts only. Hidden when the
              user has never been charged (e.g. free tier or Apple-IAP
              subscriber). Each row links out to the Stripe-hosted
              receipt + PDF — the actual billing artifacts live on
              Stripe and we don't proxy them. */}
          {invoices && invoices.length > 0 && (
            <Section label="Billing history">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderBottom: `1px solid ${v('ruleSoft')}`,
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: v('ink') }}>
                        {formatMoney(inv.amountPaid, inv.currency)}
                        {inv.status !== 'paid' && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              color: v('inkMute'),
                              marginLeft: 8,
                            }}
                          >· {inv.status}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: v('inkMute'), marginTop: 2 }}>
                        {formatDate(inv.createdAt)}
                        {inv.number && <> · {inv.number}</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {inv.hostedUrl && (
                        <a
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hm-action"
                          style={{
                            ...secondaryButtonStyle,
                            padding: '4px 10px',
                            fontSize: 11,
                            textDecoration: 'none',
                          }}
                          title="View receipt"
                        >Receipt</a>
                      )}
                      {inv.pdfUrl && (
                        <a
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hm-action"
                          style={{
                            ...secondaryButtonStyle,
                            padding: '4px 10px',
                            fontSize: 11,
                            textDecoration: 'none',
                          }}
                          title="Download PDF"
                        >PDF</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Invite friends — Web Share API on supported browsers
              (mobile Safari, Chrome Android, etc.); falls back to
              copy-to-clipboard. The shared URL embeds ?from=<name> so
              we can surface "Ben invited you" in the auth modal when
              their friend lands. */}
          {auth.user && (
            <Section label="Invite friends">
              <div style={{ fontSize: 12, color: v('inkSoft'), marginBottom: 10, lineHeight: 1.5 }}>
                Send a friend the daily puzzle — see their times on the Friends leaderboard tab once they sign up.
              </div>
              <button
                type="button"
                className="hm-action"
                onClick={async () => {
                  const name = auth.user!.displayName;
                  const url = `https://hexponents.com/?from=${encodeURIComponent(name)}`;
                  const shareData = {
                    title: 'Hexponents — daily logic puzzle',
                    text: `${name} invited you to play today's hex.`,
                    url,
                  };
                  // Native share sheet first (Web Share API), copy
                  // fallback for desktop Chrome/Firefox where the API
                  // is gated to mobile.
                  if (typeof navigator.share === 'function') {
                    try {
                      await navigator.share(shareData);
                      return;
                    } catch {
                      // User canceled — silent. Fall through to copy
                      // only if it was a no-share-handler error rather
                      // than a deliberate cancel; easiest to just exit.
                      return;
                    }
                  }
                  try {
                    await navigator.clipboard.writeText(url);
                    setShareCopied(true);
                    window.setTimeout(() => setShareCopied(false), 2200);
                  } catch {
                    window.prompt('Copy this link to share:', url);
                  }
                }}
                style={primaryButtonStyle}
              >
                {shareCopied ? '✓ Link copied' : 'Share invite link'}
              </button>
            </Section>
          )}

          <Section label="Preferences">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: v('inkSoft'), cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hapticsOn}
                onChange={(e) => {
                  const next = e.target.checked;
                  setHapticsOnLocal(next);
                  setHapticsEnabled(next);
                  // Fire a tiny test buzz so the toggle feels confirmed
                  // on devices that support it.
                  if (next) Haptics.tilePlaced();
                }}
              />
              <span>Haptic feedback (where supported)</span>
            </label>
            <div style={{ fontSize: 11, color: v('inkMute'), marginTop: 4 }}>
              Brief vibrations on tile placements + solves. Works on most Android browsers; iOS Safari doesn't expose the API.
            </div>
          </Section>

          <Section label="Sign out">
            <button
              className="hm-action"
              onClick={async () => {
                await auth.logout();
                // Hard-nav home so any page-specific state (open
                // modals, profile/browse/admin chrome, designer
                // paywall) doesn't linger after the user signs out.
                // Lands them on the main puzzle in anonymous mode —
                // most natural place to keep playing or sign back in.
                window.location.href = '/';
              }}
              style={secondaryButtonStyle}
            >Sign out of Hexponents</button>
          </Section>

          <Section label="Danger zone">
            <div style={{ fontSize: 12, color: v('inkSoft'), marginBottom: 10, lineHeight: 1.5 }}>
              Permanently delete your account. This removes your solves,
              streak, custom puzzles, badges, and follow connections.
              Active subscriptions are canceled immediately.{' '}
              <strong style={{ color: v('ink') }}>This can't be undone.</strong>
            </div>
            <button
              type="button"
              className="hm-action"
              onClick={() => setConfirmingDelete(true)}
              style={dangerButtonStyle}
            >Delete account</button>
          </Section>
        </div>
      </div>

      {embeddedCheckout && (
        <CheckoutModal
          product={embeddedCheckout}
          onClose={() => setEmbeddedCheckout(null)}
          onSuccess={onChangeCardSuccess}
        />
      )}

      {confirmingCancel && sub && (
        <ConfirmCancelDialog
          currentPeriodEnd={sub.currentPeriodEnd}
          inFlight={actionInFlight}
          onCancel={() => setConfirmingCancel(false)}
          onConfirm={async () => {
            setActionInFlight(true);
            const ok = await cancelSubscription();
            setActionInFlight(false);
            setConfirmingCancel(false);
            if (ok) await reload();
          }}
        />
      )}

      {confirmingDelete && (
        <ConfirmDeleteAccountDialog
          hasActiveStripeSub={!!sub && !sub.cancelAtPeriodEnd && sub.status === 'active'}
          isPremium={!!auth.user?.isPremium}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            setActionInFlight(true);
            const r = await apiFetch('/auth/me', {
              method: 'DELETE',
              body: JSON.stringify({ confirm: 'DELETE my account' }),
            });
            setActionInFlight(false);
            if (r.ok) {
              // Server cleared the auth cookies; reload so the
              // anonymous shell takes over and there's no chance of
              // half-stale React state holding a deleted user.
              window.location.href = '/';
              return;
            }
            const body = await r.json().catch(() => ({}));
            alert(`Couldn't delete the account: ${body.error ?? 'unknown error'}. Try again or contact support.`);
            setConfirmingDelete(false);
          }}
          inFlight={actionInFlight}
        />
      )}
    </>
  );
}

// MARK: – Pieces

function Section({ label, right, children }: { label: string; right?: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        borderTop: `1px solid ${v('ruleSoft')}`,
        paddingTop: 14,
        paddingBottom: 14,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', color: v('inkMute'), textTransform: 'uppercase' }}>{label}</div>
        {right && <div style={{ fontSize: 13, color: v('inkSoft') }}>{right}</div>}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={{ fontSize: 13, color: v('inkSoft') }}>{label}</span>
      <span style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

function ConfirmCancelDialog({
  currentPeriodEnd,
  inFlight,
  onCancel,
  onConfirm,
}: {
  currentPeriodEnd: string | null;
  inFlight: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1200, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: v('paper'), color: v('ink'),
          border: `1px solid ${v('rule')}`, borderRadius: 14,
          padding: 24, maxWidth: 380, width: '100%',
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Cancel Premium?</div>
        <div style={{ fontSize: 13, color: v('inkSoft'), marginBottom: 16, lineHeight: 1.4 }}>
          {currentPeriodEnd
            ? <>You'll keep Premium access until <strong>{formatDate(currentPeriodEnd)}</strong>. You won't be charged again. You can resume any time before then.</>
            : <>You'll keep Premium access through your current billing period. You can resume any time before it ends.</>}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="hm-action" style={secondaryButtonStyle}>Keep Premium</button>
          <button onClick={onConfirm} disabled={inFlight} className="hm-action" style={dangerButtonStyle}>
            {inFlight ? 'Canceling…' : 'Cancel subscription'}
          </button>
        </div>
      </div>
    </div>
  );
}

const DELETE_CONFIRM_PHRASE = 'DELETE my account';

// Two-step destructive flow: user must (1) acknowledge the irreversible
// effects (a list of what gets deleted + active-sub cancel notice) and
// (2) type the exact confirmation phrase. The submit button stays
// disabled until both gates pass. Phrase match is case-sensitive
// because that's how the server enforces it — no client-side
// normalization to mask a server reject.
function ConfirmDeleteAccountDialog({
  hasActiveStripeSub,
  isPremium,
  inFlight,
  onCancel,
  onConfirm,
}: {
  hasActiveStripeSub: boolean;
  isPremium: boolean;
  inFlight: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const phraseMatches = typed === DELETE_CONFIRM_PHRASE;
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1200, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: v('paper'), color: v('ink'),
          border: `1px solid ${v('bad')}`, borderRadius: 14,
          padding: 24, maxWidth: 440, width: '100%',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: v('bad'), marginBottom: 8 }}>
          Permanent
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 12 }}>Delete your account?</div>
        <div style={{ fontSize: 13, color: v('inkSoft'), marginBottom: 14, lineHeight: 1.5 }}>
          We'll permanently remove:
        </div>
        <ul style={{ fontSize: 13, color: v('inkSoft'), margin: '0 0 16px', paddingLeft: 20, lineHeight: 1.55 }}>
          <li>Your solves, streak, and badges</li>
          <li>All custom puzzles you've authored (existing share links break)</li>
          <li>Followers, follows, votes, and reports</li>
          <li>Linked Google / Apple sign-in identities</li>
        </ul>
        {hasActiveStripeSub && (
          <div style={{
            fontSize: 12, color: v('inkSoft'), marginBottom: 14, lineHeight: 1.5,
            padding: '10px 12px', borderRadius: 8,
            background: v('paperDeep'), border: `1px solid ${v('ruleSoft')}`,
          }}>
            <strong style={{ color: v('ink') }}>Heads up:</strong> your active
            Stripe subscription will be canceled immediately. The remaining
            time on this billing period is forfeited. We don't auto-refund —
            email <a href="mailto:support@hexponents.com" style={{ color: v('inkSoft'), textDecoration: 'underline' }}>support@hexponents.com</a> if you want one.
          </div>
        )}
        {isPremium && !hasActiveStripeSub && (
          <div style={{
            fontSize: 12, color: v('inkSoft'), marginBottom: 14, lineHeight: 1.5,
            padding: '10px 12px', borderRadius: 8,
            background: v('paperDeep'), border: `1px solid ${v('ruleSoft')}`,
          }}>
            <strong style={{ color: v('ink') }}>Heads up:</strong> if your
            premium came from the iOS App Store, you'll need to cancel the
            subscription separately in <em>Settings → Apple ID → Subscriptions</em>
            on your device. We can't cancel it from here.
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: v('inkSoft') }}>
            To confirm, type <strong style={{ color: v('ink'), fontFamily: '"IBM Plex Mono", monospace' }}>DELETE my account</strong>
          </span>
          <input
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            autoComplete="off"
            autoFocus
            style={{
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 14,
              padding: '8px 12px',
              borderRadius: 8,
              background: v('paper'),
              color: v('ink'),
              border: `1px solid ${phraseMatches ? v('bad') : v('rule')}`,
              outline: 'none',
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} className="hm-action" style={secondaryButtonStyle} disabled={inFlight}>Keep my account</button>
          <button
            onClick={onConfirm}
            disabled={inFlight || !phraseMatches}
            className="hm-action"
            style={dangerButtonStyle}
            title={!phraseMatches ? "Type the confirmation phrase first" : undefined}
          >
            {inFlight ? 'Deleting…' : 'Permanently delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// MARK: – Helpers

function prettyPlan(plan: string): string {
  switch (plan) {
    case 'premium_monthly': return 'Premium — Monthly';
    case 'premium_yearly':  return 'Premium — Yearly';
    case 'free':            return 'Free';
    default:                return plan;
  }
}
function prettyStatus(status: string, cancelAtPeriodEnd: boolean): string {
  if (cancelAtPeriodEnd) return 'Canceling at period end';
  switch (status) {
    case 'active':   return 'Active';
    case 'past_due': return 'Past due';
    case 'canceled': return 'Canceled';
    case 'paused':   return 'Paused';
    default:         return status;
  }
}
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/// Format cents to a human-readable currency string. Stripe returns
/// `amount_paid` in the smallest unit (cents for USD); JPY/KRW etc.
/// are zero-decimal but the catalog ships USD only today.
function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

// Inline button styles — we keep these here since AccountModal is the
// only consumer right now. If a third UI surface needs the same pill
// look, lift to a shared button component.
const primaryButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: v('ink'),
  color: v('paper'),
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 13,
};
const secondaryButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: 'transparent',
  color: v('ink'),
  border: `1px solid ${v('rule')}`,
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: 13,
};
const dangerButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: 'transparent',
  color: v('bad'),
  border: `1px solid ${v('bad')}`,
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: 13,
};
